import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Table, TableHead, TableBody, TableCell, TableContainer, 
  TableRow, Paper, Button, Typography, TextField } from '@mui/material';
import ErrorDialog from '../ErrorDialog';
import YesNoDialog from '../YesNoDialog';
import { useParams } from 'react-router-dom';
import { rpc } from '../utils/RpcUtils';
import * as Constants from '../Constants';
import { DateTime } from 'luxon';
import EventStatus from './EventStatus';
import { AuthContext } from '../AuthContextProvider';
import { Device } from '@twilio/voice-sdk';
import { fetchJson, fetchWithTimeout } from '../utils/RpcUtils';
import { usePreventReload } from '../utils/usePreventReload';
import '../App.css';

const VideoEvent = () => {
  const [url, setUrl] = useState('');
  const [scheduledEvent, setScheduledEvent] = useState(null);
  const [eventUsers, setEventUsers] = useState([]);
  const [status, setStatus]= useState(scheduledEvent ? EventStatus.getEventStatus(scheduledEvent) : 
    Constants.EVENT_NOT_READY);
  const [conferenceStarted, setConferenceStarted] = useState(false);
  const [potsSpeaker, setPotsSpeaker] = useState('');
  const [potsStatus, setPotsStatus] = useState({});
  const [hasMuted, setHasMuted] = useState({});
  
  // Audio & Network Control Refs
  const wsClient = useRef(null);
  const twilioDevice = useRef(null);
  const pollingTimer = useRef(null);
  const wsReconnectTimer = useRef(null);
  const hasHungUp = useRef({});
  const urlWindow = useRef(null);
  
  // Retries & Reconnect Guard Refs
  const maxRetries = 3;
  const retryCount = useRef(0);
  const isManualDisconnect = useRef(false);
  const isConnecting = useRef(false);
  const freshParams = useRef({});

  // UI Dialog States
  const [errorMessage, setErrorMessage] = useState('');
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [openConfirmCancelDialog, setOpenConfirmCancelDialog] = useState(false);
  const [openConfirmEndConferencelDialog, setOpenConfirmEndConferenceDialog] = useState(false);
  const [openStopVideoConfirmDialog, setOpenStopVideoConfirmDialog] = useState(false);
  const { id } = useParams();
  const { user } = useContext(AuthContext);

  // Prevent browser back button navigation during active session
  usePreventReload(true);
  useEffect(() => {
    window.history.pushState(null, null, window.location.href);
    window.onpopstate = () => window.history.go(1);
  }, []);

  useEffect(() => {
    const fetchEvent = async (id) => {
      const params = { _id: id };
      await rpc({
        method: 'GetScheduledEvent',
        params,
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: setScheduledEvent
      });
    };
    fetchEvent(id);
  }, [id]);

  useEffect(() => {
    const onFetchSuccess = (usersInGroup) => {
      setEventUsers((prevUsers) => {
        const allUsers = prevUsers.concat(usersInGroup);
        const uniqueIds = new Set();
        return allUsers.filter((u) => {
          const isDuplicate = uniqueIds.has(u._id);
          uniqueIds.add(u._id);
          return !isDuplicate;
        });
      });
    };
  
    const fetchUsersInGroup = async (group_id) => {
      const params = { _eventGroups: group_id };
      rpc({
        method: 'GetUsers',
        params,
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: onFetchSuccess
      });
    };
    if (scheduledEvent !== null) {
      setStatus(EventStatus.getEventStatus(scheduledEvent));
      scheduledEvent._eventGroups.forEach(group => {
        fetchUsersInGroup(group);
      });
    }
  }, [scheduledEvent]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (scheduledEvent !== null) {
        setStatus(EventStatus.getEventStatus(scheduledEvent));
      }
    }, 5000);   
    return () => clearInterval(timer);
  }, [scheduledEvent]);

  /****************************************************************************/
  
  const getPhoneNumber = useCallback((user) => {
    return user?.phone ? user.phone.replace(/\D/g, '') : '';
  }, []);

  const getPhoneNumbersToDial = useCallback(() => {
    const pots = [];
    eventUsers.forEach(u => {
      if (!u.useTwilio) {
        const muted = hasMuted[u.email] ? true : false;
        pots.push(`${getPhoneNumber(u)}:${muted}:${u.email}`);
      }
    });
    return pots.join(', ');
  }, [eventUsers, hasMuted, getPhoneNumber]);

  useEffect(() => {
    freshParams.current = {
      To: getPhoneNumbersToDial(),
      site: user?._site?._id,
      eventid: scheduledEvent?._id,
    };
  }, [getPhoneNumbersToDial, scheduledEvent, user]);

  /****************************************************************************/

  const hangup = useCallback(async (eventUser) => {
    try {
      const phone = getPhoneNumber(eventUser);
      hasHungUp.current[phone] = true;
      const params = new URLSearchParams({
        site: user._site._id,
        number: phone,
        email: eventUser.email,
        eventid: scheduledEvent._id
      });
      await fetchJson('/itvs-site/delete?' + params, {
        headers : { 'Accept': 'application/json' }
      });
    } catch (err) {
      console.error(err);
      setErrorMessage(`hangup ${err}`);
      setOpenErrorDialog(true);
    }
  }, [user, scheduledEvent, getPhoneNumber]);

  const hangupAll = useCallback(() => {
    eventUsers.forEach(u => {
      if (!u.useTwilio) {
        hangup(u);
      }
    });
  }, [eventUsers, hangup]);

  const stopPollingForUserStatus = useCallback(() => {
    if (pollingTimer.current) clearInterval(pollingTimer.current);
    if (wsReconnectTimer.current) clearTimeout(wsReconnectTimer.current);
    if (wsClient.current) { 
      wsClient.current.onclose = null; // Prevent reconnect loop on clean close
      wsClient.current.close();
      wsClient.current = null;
    }
  }, []);

  const startPollingForUserStatus = useCallback(() => {
    stopPollingForUserStatus();

    const connectWs = () => {
      if (isManualDisconnect.current) return;

      wsClient.current = new WebSocket(Constants.WEBSOCKET_URL);

      wsClient.current.onopen = () => {
        pollingTimer.current = setInterval(() => {
          if (wsClient.current?.readyState === WebSocket.OPEN) {
            wsClient.current.send(JSON.stringify({
              type: "getPotsSpeaker",
              site: user?._site?._id,
              eventid: scheduledEvent?._id
            }));
          }
        }, 1000);
      };

      wsClient.current.onmessage = (message) => {
        try {
          const m = JSON.parse(message.data);
          if (m.type === 'getPotsSpeaker') {
            setPotsSpeaker(m.speaker);
            setPotsStatus(m.status);
          }
        } catch (e) {
          console.error('Failed to parse WS message:', e);
        }
      };

      wsClient.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      wsClient.current.onclose = () => {
        if (pollingTimer.current) clearInterval(pollingTimer.current);

        // Auto-heal WebSocket if network drops mid-conference
        if (!isManualDisconnect.current) {
          wsReconnectTimer.current = setTimeout(() => {
            connectWs();
          }, 3000);
        }
      };
    };

    connectWs();
  }, [stopPollingForUserStatus, user, scheduledEvent]);

  const endConference = useCallback(() => {
    isManualDisconnect.current = true;
    isConnecting.current = false;
    retryCount.current = 0;

    stopPollingForUserStatus();
    setOpenConfirmEndConferenceDialog(false);
    setConferenceStarted(false);
    setPotsSpeaker('');
    setPotsStatus({});

    if (twilioDevice.current !== null) {
      try {
        twilioDevice.current.disconnectAll();
        twilioDevice.current.destroy();
      } catch (err) {
        console.error("Error cleaning Twilio Device", err);
      }
      twilioDevice.current = null;
    }
    hangupAll();
  }, [stopPollingForUserStatus, hangupAll]);

  // Keep reference for component unmount cleanup without ESLint warning
  const endConferenceRef = useRef(endConference);
  useEffect(() => {
    endConferenceRef.current = endConference;
  }, [endConference]);

  useEffect(() => {
    return () => {
      endConferenceRef.current();
    };
  }, []);

  const setEventStatus = useCallback(async ({status, url}) => {
    EventStatus.setEventStatus(scheduledEvent, status);
    setStatus(status);
    const params = {
      _id: scheduledEvent._id,
      status,
    };
    if (status === Constants.EVENT_SHOW_URL) {
      params.url = url;
    }
    rpc({
      method: 'UpdateScheduledEvent',
      params,
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess: () => { console.log("UpdateScheduleEvent success"); }
    });
  }, [scheduledEvent]);

  const startConference = useCallback(async () => {
    if (isConnecting.current) return;
    isConnecting.current = true;
    isManualDisconnect.current = false;
    hasHungUp.current = {};

    try {
      const token = await fetchJson(`/itvs-site/capability-token?identity=${encodeURIComponent(user.email)}`, {
        headers : { 'Accept': 'application/json' },
      });

      if (twilioDevice.current) {
        twilioDevice.current.destroy();
        twilioDevice.current = null;
      }

      twilioDevice.current = new Device(token.token, {
        logLevel: 'warn',
        closeProtection: true,
        edge: ['ashburn', 'roaming'],
        maxCallSignalingTimeoutMs: 30000,
      });

      twilioDevice.current.on('error', (error) => {
        console.error('Device Error:', error.message, error);
        
        const isAuthError = error.code === 31204 || error.code === 31205;

        if (!isManualDisconnect.current && !isAuthError && retryCount.current < maxRetries) {
          retryCount.current += 1;
          isConnecting.current = false;
          const delay = Math.min(1000 * Math.pow(2, retryCount.current), 8000);
          console.warn(`Reconnecting attempt ${retryCount.current} in ${delay}ms...`);
          setTimeout(() => startConference(), delay);
        } else if (retryCount.current >= maxRetries) {
          setErrorMessage('Telecon connection lost. Maximum reconnect attempts reached.');
          setOpenErrorDialog(true);
          endConference();
        }
      });

      twilioDevice.current.on('registered', () => {
        retryCount.current = 0;
      });

      const callParams = {
        ...freshParams.current,
        Initialize: true,
      };

      const call = await twilioDevice.current.connect({ params: callParams });
      setConferenceStarted(true);
      isConnecting.current = false;

      call.on('reconnecting', (error) => {
        console.warn('Network fluctuating. Reconnecting audio call...', error);
      });

      call.on('reconnected', () => {
        console.log('Audio connection recovered!');
      });

      call.on('disconnect', () => {
        if (!isManualDisconnect.current && retryCount.current < maxRetries) {
          twilioDevice.current?.emit('error', { message: 'Unexpected disconnect' });
        }
      });

      startPollingForUserStatus();
      setTimeout(() => {
        setEventStatus({ status: Constants.EVENT_TELECON_STARTED });
      }, 1000);

    } catch (err) {
      isConnecting.current = false;
      console.error("Unexpected Error in startConference", err);
      setErrorMessage(err.message || 'Failed to start conference');
      setOpenErrorDialog(true);
    }
  }, [endConference, setEventStatus, startPollingForUserStatus, user?.email]);

  /****************************************************************************/

  const getPotsStatus = useCallback((user) => {
    const phone = getPhoneNumber(user);
    const email = user.email;
    if (potsStatus[phone]) return potsStatus[phone];
    if (potsStatus[email]) return potsStatus[email];
    return 'unknown';
  }, [getPhoneNumber, potsStatus]);

  const isSpeaking = useCallback((user) => {
    return potsSpeaker === getPhoneNumber(user);
  }, [getPhoneNumber, potsSpeaker]);

  /****************************************************************************/

  const redial = async (eventUser) => {
    try {
      const phone = getPhoneNumber(eventUser);
      const muted = hasMuted[eventUser.email] ? true : false;
      hasHungUp.current[phone] = false;
      const to = `${phone}:${muted}:${eventUser.email}`;
      const params = new URLSearchParams();
      params.append('site', user._site._id);
      params.append('eventid', scheduledEvent._id);
      params.append('To', to);
      await fetchWithTimeout('/itvs-site/conference2', {
        method: "post",
        headers : { 'Accept': 'application/json' },
        body: params,
      });
    } catch (err) {
      console.error(err);
      setErrorMessage(`failed to redial ${err}`);
      setOpenErrorDialog(true);
    }
  };

  const muteOrUnmute = async (status, eventUser) => {
    try {
      const muted = status === Constants.USER_MUTED;
      const value = {[eventUser.email]: muted};
      setHasMuted(prev => ({ ...prev, ...value }));
      
      const updatePrams = {
        _id: eventUser._id,
        status
      };
      await rpc({
        method: 'UpdateUser',
        params: updatePrams,
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: (u) => { console.log("UpdateUser success", u); }
      });
      if (!eventUser.useTwilio) {
        const params = new URLSearchParams({
          site: user._site._id,
          number: getPhoneNumber(eventUser),
          muted,
          email: eventUser.email,
          eventid: scheduledEvent._id
        });
        await fetchJson('/itvs-site/mute?' + params, {
          headers : { 'Accept': 'application/json' }
        });
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(`muteOrUnmute ${err}`);
      setOpenErrorDialog(true);
    }
  };

  const muteAll = () => {
    eventUsers.forEach(u => muteOrUnmute(Constants.USER_MUTED, u));
  };

  const unMuteAll = () => {
    eventUsers.forEach(u => muteOrUnmute(Constants.USER_UNMUTED, u));
  };

  /****************************************************************************/

  const onStart = async () => setEventStatus({status: Constants.EVENT_STARTED});
  const onStop = async () => setEventStatus({status: Constants.EVENT_STOPPED});

  const onShowUrl = async () => {
    setOpenStopVideoConfirmDialog(false);
    if (url === '') {
      setErrorMessage('Please enter a URL');
      setOpenErrorDialog(true);
      return;
    }
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      setErrorMessage('Please enter a valid URL starting with http:// or https://');
      setOpenErrorDialog(true);
      return;
    }
    if (urlWindow.current != null) {
      urlWindow.current.close();
    }
    urlWindow.current = window.open(url, "_blank",
      "toolbar=no, titlebar=no, menubar=no, status=no, location=no, top=0, left=0, width=800, height=500");
    setEventStatus({status: Constants.EVENT_SHOW_URL, url});
  };

  const onCancelYes = async () => {
    if (conferenceStarted) {
      endConference();
    }
    if (urlWindow.current != null) {
      urlWindow.current.close();
    }
    setEventStatus({status: Constants.EVENT_CANCELLED});
    setOpenConfirmCancelDialog(false);
  };

  const getTitle = () => {
    if (scheduledEvent === null) return '';
    const shownOn = DateTime.fromISO(scheduledEvent.shownOn)
      .setZone(user._site.timezone)
      .toFormat(Constants.TIME_FORMAT_LUXON);
    const statusText= Constants.EVENT_STATUS_TEXT[status];
    return `${scheduledEvent._event.displayName} ${shownOn} ${statusText}`;
  };
 
  const canStart = () => status === Constants.EVENT_STOPPED || status === Constants.EVENT_TELECON_STARTED;
  const canStop = () => status === Constants.EVENT_STARTED;
  const canCancel = () => status === Constants.EVENT_STOPPED || status === Constants.EVENT_SHOW_URL || status === Constants.EVENT_TELECON_STARTED;
  const canMute = () => status !== Constants.EVENT_CANCELLED && status !== Constants.EVENT_NOT_READY && status !== Constants.EVENT_ABOUT_TO_START;
  const canShowUrl = () => status === Constants.EVENT_STOPPED || status === Constants.EVENT_SHOW_URL;
  const canStartConference = () => !conferenceStarted && (status === Constants.EVENT_STOPPED || status === Constants.EVENT_STARTED || status === Constants.EVENT_TELECON_STARTED);

  const getPhoneButtonCells = (user) => {
    if (!user.useTwilio) {
      const phone = getPhoneNumber(user);
      if (hasHungUp.current[phone] === true) {
        return (
          <TableCell>
            <Button className="UserButton" variant="contained" color="primary"
              disabled={!conferenceStarted} onClick={() => redial(user)}>
              ReDial
            </Button>
          </TableCell>
        );
      } else {
        return (
          <TableCell>
            <Button className="UserButton" variant="contained" color="primary"
              disabled={!conferenceStarted} onClick={() => hangup(user)}>
              Hangup
            </Button>
          </TableCell>
        );
      }
    }
    return <TableCell></TableCell>;
  };

  const getMuteCells = (user) => {
    if (hasMuted[user.email]) {
      return (
        <TableCell>
          <Button className="UserButton" variant="contained" color="primary"
            disabled={!conferenceStarted} onClick={() => muteOrUnmute(Constants.USER_UNMUTED, user)}>
            UnMute
          </Button>
        </TableCell>
      );
    } else {
      return (
        <TableCell>
          <Button className="UserButton" variant="contained" color="primary"
            disabled={!conferenceStarted} onClick={() => muteOrUnmute(Constants.USER_MUTED, user)}>
            Mute
          </Button>
        </TableCell>
      );
    }
  };

  return (
    <div className="VideoEventDetails">
      <Typography variant="h6" gutterBottom>
        {getTitle()}
      </Typography>
      <div className="HorizontalContainer">
        <Button className="Button" variant="contained" color="primary" 
          disabled={!canShowUrl()}
          onClick={() => {
            if (status !== Constants.EVENT_SHOW_URL) {
              setOpenStopVideoConfirmDialog(true);
            } else {
              onShowUrl();
            }
          }}>
          Show URL
        </Button>
        <TextField className="TextField"
          label="URL"
          value={url}
          size="small"
          onChange={(e) => setUrl(e.target.value)}
        />
      </div>
      <div className="HorizontalContainer">
        <Button className="Button" variant="contained" color="primary" 
          disabled={!canStart()} onClick={onStart}>
          Start
        </Button>
        <Button className="Button" variant="contained" color="primary" 
          disabled={!canStop()} onClick={onStop}>
          Stop
        </Button>
        <Button className="Button" variant="contained" color="primary" 
          disabled={!canCancel()} onClick={() => setOpenConfirmCancelDialog(true)}>
          Cancel
        </Button>
        <Button className="Button" variant="contained" color="primary" 
          disabled={!canStartConference()} onClick={startConference}>
          Start Telecon
        </Button>
        <Button className="Button" variant="contained" color="primary" 
          disabled={!conferenceStarted} onClick={() => setOpenConfirmEndConferenceDialog(true)}>
          End Telecon
        </Button>
        <Button className="Button" variant="contained" color="primary" 
          disabled={!canMute()} onClick={muteAll}>
          Mute All
        </Button>
        <Button className="Button" variant="contained" color="primary" 
          disabled={!canMute()} onClick={unMuteAll}>
          UnMute All
        </Button>
      </div>
      <TableContainer className="TableContainer" component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '10%' }}>Name</TableCell>
              <TableCell sx={{ width: '10%' }}>Phone</TableCell>
              <TableCell sx={{ width: '10%' }}>Status</TableCell>
              <TableCell sx={{ width: '10%' }}>Dial</TableCell>
              <TableCell sx={{ width: '10%' }}>Mute</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {eventUsers.map((u, index) => (
              <TableRow key={index}>
                <TableCell>{`${u.first} ${u.last}`}</TableCell>
                <TableCell style={isSpeaking(u) ? {color: 'red'} : {}}>{getPhoneNumber(u)}</TableCell>
                <TableCell>{getPotsStatus(u)}</TableCell>
                {getPhoneButtonCells(u)}
                {getMuteCells(u)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ErrorDialog open={openErrorDialog} 
        onErrorDialogClose={() => setOpenErrorDialog(false)}
        errorMessage={errorMessage} />
      <YesNoDialog open={openConfirmCancelDialog} 
        onErrorDialogClose={() => setOpenConfirmCancelDialog(false)}
        onYes={onCancelYes} 
        title="Cancel Event"
        message="Are you sure you want to cancel this event?"
      />
      <YesNoDialog open={openConfirmEndConferencelDialog} 
        onErrorDialogClose={() => setOpenConfirmEndConferenceDialog(false)}
        onYes={endConference} 
        title="End Teleconference"
        message="Are you sure you want to end the conference?"
      />
      <YesNoDialog open={openStopVideoConfirmDialog} 
        onErrorDialogClose={() => setOpenStopVideoConfirmDialog(false)}
        onYes={onShowUrl} 
        title="End Video"
        message="Are you sure you want to stop the video?"
      />
    </div>
  );
};

export default VideoEvent;